import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  BackgroundVariant,
  ConnectionMode,
  MarkerType,
  useReactFlow,
  useNodesState,
  type Edge,
  type Node,
  type Connection,
  type NodeMouseHandler,
  type OnNodeDrag,
  type EdgeMouseHandler,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import Dagre from "@dagrejs/dagre";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Download, Eye, HelpCircle, Plus, Trash2, X } from "lucide-react";
import apiClient from "@/lib/api-client";
import { toast } from "@/lib/toast";
import { ClaimNode, type ClaimNodeData } from "./ClaimNode";
import {
  useClaims,
  useClaimLinks,
  useClaimEvidence,
  useClaimActions,
  useEvidenceActions,
  type Claim,
  type ClaimEvidence as ClaimEvidenceType,
  type ClaimLink,
} from "@/hooks/useClaims";
import { useEvidenceStore, type PendingEvidence } from "@/stores/evidence-store";
import { SELECTABLE_ROLES } from "@/lib/claimRoles";
import { claimEdgeTypes, FloatingConnectionLine } from "./claimFloatingEdge";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { UpgradeCard } from "@/components/UpgradeCard";
import { usePaperRefs } from "@/hooks/usePaperRefs";
import { useNavigate } from "@tanstack/react-router";

const nodeTypes = { claimNode: ClaimNode };

// Optimistic links/claims carry a "temp-" id until the server responds. Their
// edit/delete controls must be inert — sending a temp id to a UUID path 422s.
const isTempId = (id: string) => id.startsWith("temp-");

const EDGE_COLORS: Record<string, string> = {
  supports: "#22c55e",
  challenges: "#ef4444",
  depends_on: "#3b82f6",
  qualifies: "#f59e0b",
};

const EDGE_STYLES: Record<string, string> = {
  supports: "solid",
  challenges: "dashed",
  depends_on: "dotted",
  qualifies: "dashed",
};

// Selectable link relations — the evaluative set, identical to the evidence
// vocabulary. "depends_on" (a structural prerequisite, not a stance) is retired
// from the picker; EDGE_COLORS/EDGE_STYLES keep its mapping so any pre-existing
// depends_on edge still renders, and the backend still accepts it.
const RELATION_OPTIONS = [
  { value: "supports", label: "Supports", color: "bg-green-600 hover:bg-green-700" },
  { value: "challenges", label: "Challenges", color: "bg-red-600 hover:bg-red-700" },
  { value: "qualifies", label: "Qualifies", color: "bg-amber-600 hover:bg-amber-700" },
] as const;

// Both floating menus are positioned in SCREEN (client) coordinates — the same
// space flowToScreenPosition returns — and rendered via a portal to <body> with
// position:fixed. This keeps them aligned to the cursor/edge regardless of how
// the Evidence Map pane is nested or offset (notably split view, where an
// absolutely-positioned menu inside the half-width pane would land under the
// Field Debate pane). Coordinates are clamped so the menu stays on-screen.
function clampToViewport(x: number, y: number, w: number, h: number) {
  const pad = 8;
  const maxX = (typeof window !== "undefined" ? window.innerWidth : 1920) - w - pad;
  const maxY = (typeof window !== "undefined" ? window.innerHeight : 1080) - h - pad;
  return { left: Math.max(pad, Math.min(x, maxX)), top: Math.max(pad, Math.min(y, maxY)) };
}

function ConnectionPopover({
  position,
  onSelect,
  onCancel,
}: {
  position: { x: number; y: number };
  onSelect: (relation: string) => void;
  onCancel: () => void;
}) {
  const { left, top } = clampToViewport(position.x, position.y, 150, 130);
  return createPortal(
    <>
      <div className="fixed inset-0 z-[60]" onClick={onCancel} onKeyDown={(e) => e.key === "Escape" && onCancel()} />
      <div
        className="fixed z-[61] rounded-lg border bg-background p-2 shadow-lg"
        style={{ left, top }}
      >
        <p className="mb-1.5 text-[10px] font-medium text-muted-foreground">Link type:</p>
        <div className="flex flex-col gap-1">
          {RELATION_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              size="xs"
              className={`${opt.color} text-white text-xs justify-start`}
              onClick={() => onSelect(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>
    </>,
    document.body,
  );
}

function EdgeToolbar({
  position,
  currentRelation,
  onChangeRelation,
  onDelete,
  onClose,
}: {
  position: { x: number; y: number };
  currentRelation: string;
  onChangeRelation: (relation: string) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const { left, top } = clampToViewport(position.x, position.y, 130, 44);
  return createPortal(
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} onKeyDown={(e) => e.key === "Escape" && onClose()} />
      <div
        className="fixed z-[61] rounded-lg border bg-background p-2 shadow-lg"
        style={{ left, top }}
      >
        <div className="flex items-center gap-2">
          <select
            value={currentRelation}
            onChange={(e) => onChangeRelation(e.target.value)}
            className="h-6 rounded border bg-background px-1 text-xs"
          >
            {RELATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <Button
            variant="ghost"
            size="xs"
            onClick={onDelete}
          >
            <Trash2 className="size-3 text-destructive" />
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}

// --- Dagre layout ---

function getLayoutedElements(
  nodes: Node[],
  edges: Edge[],
): { nodes: Node[]; edges: Edge[] } {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 40, ranksep: 60 });

  for (const node of nodes) {
    g.setNode(node.id, { width: 200, height: 80 });
  }
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  Dagre.layout(g);

  const layoutedNodes = nodes.map((node) => {
    const pos = g.node(node.id);
    return {
      ...node,
      position: { x: pos.x - 100, y: pos.y - 40 },
    };
  });

  return { nodes: layoutedNodes, edges };
}

// --- Subcomponents ---

function PendingEvidenceTray({
  items,
  claims,
  conceptId,
  onRemoveById,
}: {
  items: PendingEvidence[];
  claims: Claim[];
  conceptId: string;
  onRemoveById: (id: string) => void;
}) {
  // One highlight expanded at a time — clicking another collapses the previous.
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (items.length === 0) return null;

  return (
    <div className="border-b p-2">
      <p className="mb-1 text-xs font-medium text-muted-foreground">
        Pending Evidence ({items.length})
      </p>
      {items.map((item) => (
        <PendingEvidenceItem
          key={item.id}
          item={item}
          claims={claims}
          conceptId={conceptId}
          onRemoveById={onRemoveById}
          expanded={expandedId === item.id}
          onToggleExpand={() =>
            setExpandedId((cur) => (cur === item.id ? null : item.id))
          }
        />
      ))}
    </div>
  );
}

function PendingEvidenceItem({
  item,
  claims,
  conceptId,
  onRemoveById,
  expanded,
  onToggleExpand,
}: {
  item: PendingEvidence;
  claims: Claim[];
  conceptId: string;
  onRemoveById: (id: string) => void;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const [attachTo, setAttachTo] = useState<string>("");
  // Row-local in-flight state — disables the whole row during attach.
  // Can't rely on `addEvidence.isPending`: useEvidenceActions(conceptId,
  // attachTo) recreates a fresh mutation when the user changes the claim
  // dropdown, yielding a new object with isPending=false and allowing a
  // second attach to a different claim before the first settles (Codex R6).
  //
  // `attachingRef` is the SYNCHRONOUS latch — a rapid cross-button click
  // (e.g. Supports then Challenges within the same render window) would
  // otherwise slip past React's async `setIsAttaching(true)`, since state
  // updates don't land until the next render. The ref closes that gap.
  // `isAttaching` drives rendering/disabled props only.
  const attachingRef = useRef(false);
  const [isAttaching, setIsAttaching] = useState(false);
  const { addEvidence } = useEvidenceActions(conceptId, attachTo);

  const handleAttach = (relation: string) => {
    if (!attachTo) return;
    if (attachingRef.current) return; // synchronous re-entrancy guard
    attachingRef.current = true;
    // Capture the stable id at click-time. The tray can mutate while the
    // POST is in flight, and index-based splice would nuke the wrong item.
    const targetId = item.id;
    setIsAttaching(true);
    addEvidence.mutate(
      {
        paper_id: item.paperId,
        excerpt_text: item.text,
        page_number: item.pageNumber,
        relation,
      },
      {
        // Remove tray entry + success toast ONLY after the server confirms.
        // Previously this ran synchronously after .mutate(); a failed POST
        // would leave the user with a missing tray row, a success toast
        // that lied, AND (since the PDF stash now creates a highlight at
        // stash time) an orphaned PaperHighlight with no claim attached.
        onSuccess: () => {
          onRemoveById(targetId);
          toast.success("Evidence attached to claim");
          // No need to reset either flag — the row unmounts on removal.
        },
        onError: () => {
          toast.error("Failed to attach evidence — try again");
          attachingRef.current = false;
          setIsAttaching(false); // allow retry
        },
      },
    );
  };

  return (
    <div className="mb-1 rounded border bg-muted/50 p-2 text-xs">
      <div className="flex items-start justify-between gap-1">
        <button
          type="button"
          onClick={onToggleExpand}
          title={expanded ? "Collapse highlight" : "Show full highlight"}
          className={`flex-1 cursor-pointer text-left ${expanded ? "whitespace-pre-wrap" : "line-clamp-2"}`}
        >
          {item.text}
        </button>
        <Button
          variant="ghost"
          size="xs"
          onClick={() => onRemoveById(item.id)}
          disabled={isAttaching}
          className="shrink-0"
        >
          <X className="size-3" />
        </Button>
      </div>
      <p className="mt-0.5 text-[10px] text-muted-foreground truncate">
        from: {item.paperTitle}
      </p>
      <div className="mt-1">
        <select
          value={attachTo}
          onChange={(e) => setAttachTo(e.target.value)}
          disabled={isAttaching}
          className="h-6 w-full rounded border bg-background px-1 text-xs"
        >
          <option value="">Attach to claim...</option>
          {claims.map((c) => (
            <option key={c.id} value={c.id}>
              {c.text.slice(0, 60)}
              {c.text.length > 60 ? "..." : ""}
            </option>
          ))}
        </select>
        <div className="mt-1 flex gap-1">
          <Button
            size="xs"
            disabled={!attachTo || isAttaching}
            onClick={() => handleAttach("supports")}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white text-[10px] px-1"
          >
            Supports
          </Button>
          <Button
            size="xs"
            disabled={!attachTo || isAttaching}
            onClick={() => handleAttach("challenges")}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white text-[10px] px-1"
          >
            Challenges
          </Button>
          <Button
            size="xs"
            disabled={!attachTo || isAttaching}
            onClick={() => handleAttach("qualifies")}
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-[10px] px-1"
          >
            Qualifies
          </Button>
        </div>
      </div>
    </div>
  );
}

function ClaimDetail({
  claim,
  evidence,
  conceptId,
  links,
  allClaims,
  onClose,
  onViewSource,
  paperLookup,
}: {
  claim: Claim;
  evidence: ClaimEvidenceType[];
  conceptId: string;
  links: ClaimLink[];
  allClaims: Claim[];
  onClose: () => void;
  onViewSource?: (paperId: string, paperTitle: string, excerptText: string, pageNumber?: number) => void;
  paperLookup?: Record<string, string>;
}) {
  const { updateClaim, deleteClaim, restoreClaim, createLink, updateLink, deleteLink } = useClaimActions(conceptId);
  const { deleteEvidence, updateEvidence } = useEvidenceActions(conceptId, claim.id);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(claim.text);
  const [addingLink, setAddingLink] = useState(false);
  const [linkTarget, setLinkTarget] = useState("");
  const [linkRelation, setLinkRelation] = useState("supports");

  const handleSaveText = () => {
    if (editText.trim() && editText !== claim.text) {
      updateClaim.mutate({ claimId: claim.id, text: editText.trim() });
    }
    setEditing(false);
  };

  const handleTypeChange = (type: string) => {
    updateClaim.mutate({ claimId: claim.id, claim_type: type });
  };

  return (
    <div className="border-t p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium">Claim Detail</span>
        <Button variant="ghost" size="xs" onClick={onClose}>
          <X className="size-3" />
        </Button>
      </div>

      {/* Editable text */}
      {editing ? (
        <div className="mb-2">
          <Input
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveText();
              if (e.key === "Escape") setEditing(false);
            }}
            onBlur={handleSaveText}
            className="text-xs"
            autoFocus
          />
        </div>
      ) : (
        <p
          className="mb-2 cursor-pointer rounded p-1 text-sm hover:bg-muted"
          onClick={() => {
            setEditText(claim.text);
            setEditing(true);
          }}
        >
          {claim.text}
        </p>
      )}

      {/* Role / open-question state + derived status */}
      {claim.claim_type === "gap" ? (
        // Open question — a flagged hole, not an asserted claim. Read-only
        // role; excluded from completeness scoring server-side. Offer to
        // promote it into a real claim by assigning a rhetorical role.
        <div className="mb-3 space-y-1.5 text-xs">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-violet-300 bg-violet-50 px-2 py-0.5 text-violet-700 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-300">
              <HelpCircle className="size-3" /> Open question
            </span>
            <span className="flex items-center gap-1" title="Derived from evidence">
              <span
                className={`inline-block size-2 rounded-full ${
                  claim.status === "contested"
                    ? "bg-amber-500"
                    : claim.status === "supported"
                      ? "bg-green-500"
                      : "bg-gray-400"
                }`}
              />
              <span className="capitalize text-muted-foreground">{claim.status}</span>
            </span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <span>Turn into a claim:</span>
            <select
              value=""
              onChange={(e) => e.target.value && handleTypeChange(e.target.value)}
              className="h-6 rounded-full border bg-background px-2 text-xs"
            >
              <option value="">Pick role…</option>
              {SELECTABLE_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="mb-3 flex items-center gap-3 text-xs">
          <label className="flex items-center gap-1">
            <span className="text-muted-foreground">Role</span>
            <select
              value={claim.claim_type}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="h-6 rounded-full border bg-background px-2 text-xs"
            >
              {SELECTABLE_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </label>
          <span className="flex items-center gap-1" title="Derived from evidence">
            <span
              className={`inline-block size-2 rounded-full ${
                claim.status === "contested"
                  ? "bg-amber-500"
                  : claim.status === "supported"
                    ? "bg-green-500"
                    : "bg-gray-400"
              }`}
            />
            <span className="capitalize text-muted-foreground">{claim.status}</span>
          </span>
          <button
            type="button"
            onClick={() => handleTypeChange("gap")}
            className="ml-auto text-[11px] text-muted-foreground hover:text-violet-500"
            title="Flag this as an open question (excluded from completeness scoring)"
          >
            Mark as open question
          </button>
        </div>
      )}

      {/* Relationships */}
      {(() => {
        const outgoing = links.filter((l) => l.source_claim_id === claim.id);
        const incoming = links.filter((l) => l.target_claim_id === claim.id);
        const claimText = (id: string) => allClaims.find((c) => c.id === id)?.text ?? "?";
        const linkableTargets = allClaims.filter(
          (c) => c.id !== claim.id && !outgoing.some((l) => l.target_claim_id === c.id),
        );
        return (
          <div className="mb-3">
            <p className="mb-1 text-xs font-medium">
              Relationships ({outgoing.length + incoming.length})
            </p>
            {outgoing.map((l) => {
              const pending = isTempId(l.id);
              return (
                <div key={l.id} className="mb-1 flex items-center gap-1 rounded border p-1.5 text-[11px]">
                  <select
                    value={l.relation}
                    disabled={pending}
                    onChange={(e) => updateLink.mutate({ linkId: l.id, relation: e.target.value })}
                    className="h-5 rounded border bg-background px-1 text-[10px] disabled:opacity-50"
                  >
                    {RELATION_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <span className="text-muted-foreground">→</span>
                  <span className="line-clamp-1 flex-1">{claimText(l.target_claim_id)}</span>
                  <Button variant="ghost" size="xs" disabled={pending} onClick={() => deleteLink.mutate(l.id)}>
                    <Trash2 className="size-3 text-destructive" />
                  </Button>
                </div>
              );
            })}
            {incoming.map((l) => (
              <div key={l.id} className="mb-1 flex items-center gap-1 rounded border border-dashed p-1.5 text-[11px] text-muted-foreground">
                <span className="line-clamp-1 flex-1">{claimText(l.source_claim_id)}</span>
                <span>{l.relation} →</span>
                <span className="italic">this</span>
              </div>
            ))}
            {addingLink ? (
              <div className="mt-1 flex items-center gap-1">
                <select
                  value={linkRelation}
                  onChange={(e) => setLinkRelation(e.target.value)}
                  className="h-6 rounded border bg-background px-1 text-[10px]"
                >
                  {RELATION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <select
                  value={linkTarget}
                  onChange={(e) => setLinkTarget(e.target.value)}
                  className="h-6 flex-1 rounded border bg-background px-1 text-[10px]"
                >
                  <option value="">Target claim…</option>
                  {linkableTargets.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.text.slice(0, 50)}{c.text.length > 50 ? "…" : ""}
                    </option>
                  ))}
                </select>
                <Button
                  size="xs"
                  disabled={!linkTarget}
                  onClick={() => {
                    createLink.mutate({
                      source_claim_id: claim.id,
                      target_claim_id: linkTarget,
                      relation: linkRelation,
                    });
                    setAddingLink(false);
                    setLinkTarget("");
                    setLinkRelation("supports");
                  }}
                >
                  Add
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setAddingLink(false)}>
                  <X className="size-3" />
                </Button>
              </div>
            ) : (
              linkableTargets.length > 0 && (
                <Button size="xs" variant="ghost" className="text-[11px]" onClick={() => setAddingLink(true)}>
                  <Plus className="mr-1 size-3" /> Link to another claim
                </Button>
              )
            )}
          </div>
        );
      })()}

      {/* Evidence list */}
      <div className="mb-2">
        <p className="mb-1 text-xs font-medium">
          Evidence ({evidence.length})
        </p>
        {evidence.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No evidence yet. Select text in a PDF and click &quot;Evidence&quot;
            to add.
          </p>
        )}
        {evidence.map((ev) => (
          <div
            key={ev.id}
            className="mb-1 rounded border p-2 text-xs"
          >
            <div className="flex items-start justify-between">
              <p className="line-clamp-3">{ev.excerpt_text}</p>
              <div className="flex shrink-0 gap-0.5">
                {onViewSource && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() =>
                      onViewSource(
                        ev.paper_id,
                        paperLookup?.[ev.paper_id] ?? "",
                        ev.excerpt_text,
                        ev.page_number ?? undefined,
                      )
                    }
                    title="View in context"
                  >
                    <Eye className="size-3" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => deleteEvidence.mutate(ev.id)}
                  className="shrink-0"
                >
                  <Trash2 className="size-3" />
                </Button>
              </div>
            </div>
            <div className="mt-1 flex items-center gap-1">
              <select
                value={ev.relation}
                onChange={(e) =>
                  updateEvidence.mutate({
                    evidenceId: ev.id,
                    relation: e.target.value,
                  })
                }
                className={`h-5 rounded-full border px-2 text-[10px] ${
                  ev.relation === "challenges"
                    ? "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-400"
                    : ev.relation === "qualifies"
                      ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400"
                      : "border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/30 dark:text-green-400"
                }`}
              >
                <option value="supports">supports</option>
                <option value="challenges">challenges</option>
                <option value="qualifies">qualifies</option>
              </select>
              {ev.page_number && (
                <span className="text-[10px] text-muted-foreground">
                  p.{ev.page_number}
                </span>
              )}
            </div>
            {ev.note && (
              <p className="mt-1 text-[10px] italic text-muted-foreground">
                {ev.note}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Delete claim */}
      <Button
        variant="destructive"
        size="xs"
        onClick={() => {
          const deletedId = claim.id;
          deleteClaim.mutate(deletedId);
          onClose();
          toast("Claim deleted", {
            description: claim.text.length > 80 ? `${claim.text.slice(0, 80)}…` : claim.text,
            duration: 10000,
            action: {
              label: "Undo",
              onClick: () => {
                restoreClaim.mutate(deletedId, {
                  onSuccess: () => toast.success("Claim restored"),
                  onError: () => toast.error("Couldn't restore claim — try again"),
                });
              },
            },
          });
        }}
      >
        <Trash2 className="mr-1 size-3" />
        Delete Claim
      </Button>
    </div>
  );
}

// --- Main panel ---

function EvidenceMapInner({
  conceptId,
  mode,
  onViewSource,
}: {
  conceptId: string;
  mode: "panel" | "fullpage";
  onViewSource?: (paperId: string, paperTitle: string, excerptText: string, pageNumber?: number) => void;
}) {
  const { data: claims = [] } = useClaims(conceptId);
  const { data: links = [] } = useClaimLinks(conceptId);
  type TitleRef = { id: string; title: string };
  const TITLE_FIELDS = ["id", "title"] as const;
  const { data: trawledRefs } = usePaperRefs<TitleRef>(conceptId, {
    fields: TITLE_FIELDS,
    role: "trawled",
  });
  const { data: foundationalRefs } = usePaperRefs<TitleRef>(conceptId, {
    fields: TITLE_FIELDS,
    role: "foundational",
  });
  const navigate = useNavigate();
  // Reached only by paid users (the outer panel gates the whole map behind
  // UpgradeCard), so the empty-state import CTA just needs a corpus to match
  // citations against.
  const hasPapers =
    (trawledRefs?.refs?.length ?? 0) + (foundationalRefs?.refs?.length ?? 0) > 0;
  const paperLookup = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const p of trawledRefs?.refs ?? []) map[p.id] = p.title;
    for (const p of foundationalRefs?.refs ?? []) map[p.id] = p.title;
    return map;
  }, [trawledRefs, foundationalRefs]);
  const { createClaim, createLink, deleteLink, updateLink, setClaimPosition } = useClaimActions(conceptId);

  const pendingEvidence = useEvidenceStore((s) => s.pendingEvidence);
  const removePendingById = useEvidenceStore((s) => s.removePendingById);
  const selectedClaimId = useEvidenceStore((s) => s.selectedClaimId);
  const selectClaim = useEvidenceStore((s) => s.selectClaim);

  const [newClaimText, setNewClaimText] = useState("");
  const [showAddClaim, setShowAddClaim] = useState(false);
const [pendingConnection, setPendingConnection] = useState<{ source: string; target: string } | null>(null);
  // Screen (client) coords of the drag release — where the relation picker opens.
  const [dropPosition, setDropPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const { flowToScreenPosition, getNode } = useReactFlow();

  // Stable no-op handler — new function refs on every render cause ReactFlow to
  // re-init internals. (Node changes are handled by useNodesState's onNodesChange
  // so drags apply; edges stay derived.)
  const noOpEdgesChange = useCallback(() => {}, []);

  // Stable ref for createLink so handleConnect doesn't change every render
  const createLinkRef = useRef(createLink);
  createLinkRef.current = createLink;

  // Build reactflow nodes & edges. Auto-layout via dagre, then override with
  // any user-set positions (a dragged node persists pos_x/pos_y — see F).
  const { nodes: layoutedNodes, edges } = useMemo(() => {
    const rfNodes: Node[] = claims.map((c) => ({
      id: c.id,
      type: "claimNode",
      position: { x: 0, y: 0 },
      // Only the grip (.claim-drag-handle) moves the card; dragging the body
      // draws a connector, a click selects. See ClaimNode.
      dragHandle: ".claim-drag-handle",
      data: {
        text: c.text,
        claimType: c.claim_type,
        status: c.status,
        evidenceCount: c.evidence_count,
        selected: c.id === selectedClaimId,
      } satisfies ClaimNodeData,
    }));

    const rfEdges: Edge[] = links.map((l) => {
      const color = EDGE_COLORS[l.relation] ?? "#888";
      return {
        id: l.id,
        source: l.source_claim_id,
        target: l.target_claim_id,
        type: "floating",
        data: { label: l.relation },
        markerEnd: { type: MarkerType.ArrowClosed, color, width: 24, height: 24 },
        style: {
          stroke: color,
          strokeDasharray: EDGE_STYLES[l.relation] === "dashed" ? "5 5" : EDGE_STYLES[l.relation] === "dotted" ? "2 2" : undefined,
        },
      };
    });

    const laid =
      rfNodes.length > 0
        ? getLayoutedElements(rfNodes, rfEdges)
        : { nodes: rfNodes, edges: rfEdges };

    const posById = new Map(claims.map((c) => [c.id, c]));
    const positioned = laid.nodes.map((n) => {
      const c = posById.get(n.id);
      return c && c.pos_x != null && c.pos_y != null
        ? { ...n, position: { x: c.pos_x, y: c.pos_y } }
        : n;
    });
    return { nodes: positioned, edges: laid.edges };
  }, [claims, links, selectedClaimId]);

  // Controlled node state so nodes can be dragged. Re-synced whenever the
  // derived layout changes (claims/links/selection); a drag doesn't change
  // `layoutedNodes`, so it never resets mid-drag.
  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
  // Re-seed the draggable node state only when the claim/link CONTENT changes
  // (positions, selection, text, status, links) — keyed on a stable signature
  // STRING, not on the array's identity. Keying on `layoutedNodes` identity
  // caused a setState loop: React Query refetch / StrictMode / ReactFlow's own
  // dimension-measurement writes produce a fresh array ref, the effect re-fires
  // and wipes ReactFlow's measured nodes, which re-measures → loop ("Maximum
  // update depth exceeded"). A signature compared by value can't churn.
  const layoutSignature = useMemo(
    () =>
      claims
        .map(
          (c) =>
            `${c.id},${c.pos_x ?? ""},${c.pos_y ?? ""},${c.claim_type},${c.status},${c.evidence_count},${c.id === selectedClaimId ? 1 : 0},${c.text}`,
        )
        .join("|") +
      "::" +
      links.map((l) => `${l.id},${l.relation}`).join("|"),
    [claims, links, selectedClaimId],
  );
  useEffect(() => {
    setNodes(layoutedNodes);
    // Resync on content signature, not array identity — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutSignature, setNodes]);

  const setPositionRef = useRef(setClaimPosition);
  setPositionRef.current = setClaimPosition;
  const handleNodeDragStop: OnNodeDrag = useCallback((_e, node) => {
    setPositionRef.current.mutate({
      claimId: node.id,
      pos_x: node.position.x,
      pos_y: node.position.y,
    });
  }, []);

  // While the relation picker is open, show the just-drawn edge immediately as a
  // dashed neutral "pending" edge so the line appears before a type is chosen.
  // Appended AFTER layout so it never perturbs the dagre ranking.
  const displayEdges = useMemo(() => {
    if (!pendingConnection) return edges;
    const pending: Edge = {
      id: "__pending__",
      source: pendingConnection.source,
      target: pendingConnection.target,
      type: "floating",
      data: {},
      selectable: false,
      markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8", width: 24, height: 24 },
      style: { stroke: "#94a3b8", strokeDasharray: "5 5" },
    };
    return [...edges, pending];
  }, [edges, pendingConnection]);

  // Toggle selection: click same node deselects, click different node selects
  const handleNodeClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      setSelectedEdgeId(null);
      const current = useEvidenceStore.getState().selectedClaimId;
      selectClaim(current === node.id ? null : node.id);
    },
    [selectClaim],
  );

  const handleEdgeClick: EdgeMouseHandler = useCallback(
    (_event, edge) => {
      // Don't open the toolbar for an unsettled (optimistic) edge — its temp id
      // can't be PATCHed/DELETEd, and the "__pending__" preview isn't editable.
      if (isTempId(edge.id) || edge.id === "__pending__") return;
      setSelectedEdgeId(edge.id);
    },
    [],
  );

  const selectedClaim = claims.find((c) => c.id === selectedClaimId);

  // Evidence for selected claim
  const { data: evidence = [] } = useClaimEvidence(conceptId, selectedClaimId);

  const handleConnect = useCallback(
    (connection: Connection) => {
      // Loose connection mode means direction follows the drag (start = source,
      // end = target). Ignore self-loops a stray drag could otherwise create.
      if (connection.source && connection.target && connection.source !== connection.target) {
        setPendingConnection({
          source: connection.source,
          target: connection.target,
        });
      }
    },
    [],
  );

  // Capture the release point so the relation picker opens at the cursor (not
  // offset off near the node) and in the correct viewport coordinate space.
  const handleConnectEnd = useCallback((event: MouseEvent | TouchEvent) => {
    const pt = "changedTouches" in event ? event.changedTouches[0] : event;
    if (pt) setDropPosition({ x: pt.clientX, y: pt.clientY });
  }, []);

  const handleRelationSelect = useCallback(
    (relation: string) => {
      if (pendingConnection) {
        createLinkRef.current.mutate({
          source_claim_id: pendingConnection.source,
          target_claim_id: pendingConnection.target,
          relation,
        });
        setPendingConnection(null);
      }
    },
    [pendingConnection],
  );

  const edgeToolbarPosition = useMemo(() => {
    if (!selectedEdgeId) return null;
    const edge = links.find((l) => l.id === selectedEdgeId);
    if (!edge) return null;
    const sourceNode = getNode(edge.source_claim_id);
    const targetNode = getNode(edge.target_claim_id);
    if (!sourceNode || !targetNode) return null;
    const midpoint = {
      x: (sourceNode.position.x + targetNode.position.x) / 2 + 110,
      y: (sourceNode.position.y + targetNode.position.y) / 2 + 40,
    };
    return flowToScreenPosition(midpoint);
  }, [selectedEdgeId, links, getNode, flowToScreenPosition]);

  const selectedEdge = links.find((l) => l.id === selectedEdgeId);

  useEffect(() => {
    if (!pendingConnection && !selectedEdgeId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPendingConnection(null);
        setSelectedEdgeId(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pendingConnection, selectedEdgeId]);

  const handleAddClaim = () => {
    const text = newClaimText.trim();
    if (!text) return;
    // Optimistic: clear input immediately. Pill stays open for rapid multi-add.
    setNewClaimText("");
    createClaim.mutate({ text });
  };

  const handleExportOutline = useCallback(async () => {
    try {
      const md = await apiClient
        .get(`api/concepts/${conceptId}/claims/export-outline`)
        .text();
      const blob = new Blob([md], { type: "text/markdown;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "argument-outline.md";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  }, [conceptId]);

  useEffect(() => {
    if (mode !== "fullpage") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement;
      if (el?.isContentEditable || el?.tagName === "INPUT" || el?.tagName === "TEXTAREA" || el?.tagName === "SELECT") {
        return;
      }
      if (e.key.toLowerCase() === "n" && !showAddClaim) {
        e.preventDefault();
        setShowAddClaim(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, showAddClaim]);

  if (mode === "fullpage") {
    return (
      <div className="relative h-full w-full">
        {claims.length === 0 ? (
          <div className="flex h-full items-center justify-center p-4 text-center text-xs text-muted-foreground">
            No claims yet. Press <kbd className="mx-1 rounded border px-1">N</kbd> or click “+ Claim” to start.
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={displayEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={noOpEdgesChange}
            onNodeClick={handleNodeClick}
            onEdgeClick={handleEdgeClick}
            onConnect={handleConnect}
            onConnectEnd={handleConnectEnd}
            onNodeDragStop={handleNodeDragStop}
            nodeTypes={nodeTypes}
            edgeTypes={claimEdgeTypes}
            connectionLineComponent={FloatingConnectionLine}
            nodesDraggable
            connectionMode={ConnectionMode.Loose}
            connectOnClick={false}
            fitView
            proOptions={{ hideAttribution: true }}
            minZoom={0.3}
            maxZoom={2}
          >
            <Controls showInteractive={false} position="bottom-right" />
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          </ReactFlow>
        )}

        {/* Floating add-claim pill (top-left) */}
        <div className="absolute left-3 top-3 z-20">
          {showAddClaim ? (
            <div className="flex items-center gap-2 rounded-lg border bg-background/95 p-1 shadow-md backdrop-blur">
              <Input
                value={newClaimText}
                onChange={(e) => setNewClaimText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddClaim();
                  if (e.key === "Escape") setShowAddClaim(false);
                }}
                placeholder="Enter claim text…  (Enter to add, Esc to close)"
                className="h-7 w-96 text-xs"
                autoFocus
              />
              <Button size="xs" onClick={handleAddClaim} disabled={!newClaimText.trim()}>Add</Button>
              <Button size="xs" variant="ghost" onClick={() => setShowAddClaim(false)}>
                <X className="size-3" />
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" className="shadow-md" onClick={() => setShowAddClaim(true)}>
              <Plus className="mr-1 size-3" /> Claim
            </Button>
          )}
        </div>

        {/* Floating export (top-right) */}
        {claims.length > 0 && (
          <div className="absolute right-3 top-3 z-20">
            <Button size="sm" variant="outline" className="shadow-md" onClick={handleExportOutline} title="Export outline">
              <Download className="size-3" />
            </Button>
          </div>
        )}

        {dropPosition && pendingConnection && (
          <ConnectionPopover position={dropPosition} onSelect={handleRelationSelect} onCancel={() => setPendingConnection(null)} />
        )}
        {edgeToolbarPosition && selectedEdge && (
          <EdgeToolbar
            position={edgeToolbarPosition}
            currentRelation={selectedEdge.relation}
            onChangeRelation={(relation) => { updateLink.mutate({ linkId: selectedEdge.id, relation }); setSelectedEdgeId(null); }}
            onDelete={() => { deleteLink.mutate(selectedEdge.id); setSelectedEdgeId(null); }}
            onClose={() => setSelectedEdgeId(null)}
          />
        )}

        {/* Right-side slide-over: pending tray + claim detail */}
        {(selectedClaim || pendingEvidence.length > 0) && (
          <div className="absolute right-0 top-0 z-30 flex h-full w-[min(360px,90%)] flex-col overflow-y-auto border-l bg-background shadow-xl">
            <PendingEvidenceTray
              items={pendingEvidence}
              claims={claims}
              conceptId={conceptId}
              onRemoveById={removePendingById}
            />
            {selectedClaim && (
              <ClaimDetail
                claim={selectedClaim}
                evidence={evidence}
                conceptId={conceptId}
                links={links}
                allClaims={claims}
                onClose={() => selectClaim(null)}
                onViewSource={onViewSource}
                paperLookup={paperLookup}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Pending evidence tray */}
      <PendingEvidenceTray
        items={pendingEvidence}
        claims={claims}
        conceptId={conceptId}
        onRemoveById={removePendingById}
      />

      {/* Add claim */}
      <div className="border-b p-2">
        {showAddClaim ? (
          <div className="flex items-center gap-1">
            <Input
              value={newClaimText}
              onChange={(e) => setNewClaimText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddClaim();
                if (e.key === "Escape") setShowAddClaim(false);
              }}
              placeholder="Enter claim text..."
              className="h-7 text-xs"
              autoFocus
            />
            <Button size="xs" onClick={handleAddClaim} disabled={!newClaimText.trim()}>
              Add
            </Button>
            <Button size="xs" variant="ghost" onClick={() => setShowAddClaim(false)}>
              <X className="size-3" />
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAddClaim(true)}
            className="w-full text-xs"
          >
            <Plus className="mr-1 size-3" />
            Add Claim
          </Button>
        )}
      </div>

      {/* Graph + detail split */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className={selectedClaim ? "flex-1 min-h-0" : "flex-1"} style={{ minHeight: 150 }}>
          {claims.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2.5 p-4 text-center">
              <p className="text-xs text-muted-foreground">
                No claims yet. Add a claim to start building your argument.
              </p>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasPapers}
                title={
                  hasPapers
                    ? "Turn an existing draft into claims"
                    : "Add papers to this concept first"
                }
                onClick={() =>
                  navigate({
                    to: "/concepts/$conceptId/import/outline",
                    params: { conceptId },
                  })
                }
              >
                Import an outline
              </Button>
              <p className="text-[11px] text-muted-foreground/70">
                Citations are matched to papers already in your corpus.
              </p>
            </div>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={displayEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={noOpEdgesChange}
              onNodeClick={handleNodeClick}
              onEdgeClick={handleEdgeClick}
              onConnect={handleConnect}
              onConnectEnd={handleConnectEnd}
            onNodeDragStop={handleNodeDragStop}
              nodeTypes={nodeTypes}
              edgeTypes={claimEdgeTypes}
              connectionLineComponent={FloatingConnectionLine}
              nodesDraggable
              connectionMode={ConnectionMode.Loose}
              connectOnClick={false}
              fitView
              proOptions={{ hideAttribution: true }}
              minZoom={0.3}
              maxZoom={2}
            >
              <Controls showInteractive={false} position="top-right" />
              <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
            </ReactFlow>
          )}
          {dropPosition && pendingConnection && (
            <ConnectionPopover
              position={dropPosition}
              onSelect={handleRelationSelect}
              onCancel={() => setPendingConnection(null)}
            />
          )}
          {edgeToolbarPosition && selectedEdge && (
            <EdgeToolbar
              position={edgeToolbarPosition}
              currentRelation={selectedEdge.relation}
              onChangeRelation={(relation) => {
                updateLink.mutate({ linkId: selectedEdge.id, relation });
                setSelectedEdgeId(null);
              }}
              onDelete={() => {
                deleteLink.mutate(selectedEdge.id);
                setSelectedEdgeId(null);
              }}
              onClose={() => setSelectedEdgeId(null)}
            />
          )}
        </div>

        {/* Claim detail */}
        {selectedClaim && (
          <div className="shrink-0 max-h-[45%] overflow-y-auto border-t">
            <ClaimDetail
              claim={selectedClaim}
              evidence={evidence}
              conceptId={conceptId}
              links={links}
              allClaims={claims}
              onClose={() => selectClaim(null)}
              onViewSource={onViewSource}
              paperLookup={paperLookup}
            />
          </div>
        )}
      </div>

      {/* Export button */}
      {claims.length > 0 && (
        <div className="border-t p-2">
          <Button
            size="sm"
            variant="outline"
            className="w-full text-xs"
            onClick={handleExportOutline}
          >
            <Download className="size-3" />
            Export outline
          </Button>
        </div>
      )}
    </div>
  );
}

// --- Exported wrapper ---

interface EvidenceMapPanelProps {
  conceptId: string;
  isOpen: boolean;
  onToggle: () => void;
  claimCount: number;
  mode?: "panel" | "fullpage";
  onViewSource?: (paperId: string, paperTitle: string, excerptText: string, pageNumber?: number) => void;
}

export function EvidenceMapPanel({
  conceptId,
  isOpen,
  onToggle,
  claimCount,
  mode = "panel",
  onViewSource,
}: EvidenceMapPanelProps) {
  const { data: currentUser } = useCurrentUser();
  const isFullpage = mode === "fullpage";

  return (
    <div
      className={
        isFullpage
          ? "flex h-full w-full flex-col border-l bg-background"
          : `border-l bg-background transition-all ${isOpen ? "w-[28rem]" : "w-0 overflow-hidden"}`
      }
    >
      {(isFullpage || isOpen) && (
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Evidence Map</span>
              {claimCount > 0 && (
                <Badge variant="secondary" className="text-[10px]">
                  {claimCount}
                </Badge>
              )}
            </div>
            {!isFullpage && (
              <Button variant="ghost" size="xs" onClick={onToggle}>
                <X className="size-4" />
              </Button>
            )}
          </div>

          <div className="flex-1 overflow-hidden">
            {!isPaidPlan(currentUser?.plan) ? (
              <UpgradeCard
                feature="Evidence Map"
                description="Build structured arguments by creating claims, linking evidence from your papers, and analyzing the strength of your reasoning."
              />
            ) : (
              <ReactFlowProvider>
                <EvidenceMapInner conceptId={conceptId} mode={mode} onViewSource={onViewSource} />
              </ReactFlowProvider>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
